import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { TabMenuModule } from 'primeng/tabmenu';
import { INotification, authUser } from '../../../interfaces';
import { Strategy } from '../../../interfaces/models/strategy.model';
import { SinceDatePipe } from '../../../pipes/since-date.pipe';
import { AuthService } from '../../../services/auth.service';
import { DirectionService } from '../../../services/direction.service';
import { NotificationService } from '../../../services/notification.service';
import { SocketService } from '../../../services/socket.service';
import { StrategyService } from '../../../services/strategy.service';
import { ThemeService } from '../../../services/theme.service';
import { SpinnerService } from '../../../services/spinner.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SignalRService } from '../../../services/signal-r.service';
import { Subject, filter, switchMap, takeUntil } from 'rxjs';

@Component({
  selector: 'app-nav',
  standalone: true,
  imports: [
    TranslateModule, TabMenuModule, RouterModule, CommonModule,
    SinceDatePipe, BadgeModule, ButtonModule],
  templateUrl: './nav.component.html',
  styleUrl: './nav.component.scss'
})
export class NavComponent implements OnInit, AfterViewInit {
  theme = 'theme'
  user!: authUser | null;
  ListInfo = false;
  currentLang = "ar"
  lang = false;
  notificationMenu = false;
  isAuthenticated = false;
  dir!: "rtl" | "ltr";
  isShortLinksMenu = false;
  notifications = [] as INotification[];
  progress = 0
  isMobile = false;
  
  // Get count of unread notifications
  get unreadCount(): number {
    return this.notifications.filter(n => !n.is_read).length;
  }
  
  constructor(
    private authService: AuthService,
    private themeService: ThemeService,
    private router: Router,
    private socketService: SocketService,
    private notificationService: NotificationService,
    private strategyService: StrategyService,
    private translate: TranslateService,
    private directionService: DirectionService,
    private renderer: Renderer2,
    private elRef: ElementRef,
    private signalRService: SignalRService,

    private spinnerService: SpinnerService,
    private observer: BreakpointObserver
  ) {
  }
  selectedStrategy = {} as Strategy;
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {

    this.authService.authBufferSub$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.user = user;
      this.isAuthenticated = !!user;

      // When logged out, clear notifications to avoid stale UI
      if (!user) {
        this.notifications = [];
      } else {
        // Ensure SignalR connection is started when a user session exists
        this.signalRService.connect().pipe(takeUntil(this.destroy$)).subscribe();
      }
    });

    // Set refresh notification strategy only when authenticated and default strategy exists
    this.authService.authBufferSub$.pipe(
      filter((u) => !!u),
      switchMap(() => this.strategyService.defaultStrategy$),
      takeUntil(this.destroy$),
    ).subscribe((strategy) => {
      this.socketService.setRefreshNotification(strategy?.id || 0);
    });

    this.socketService.refreshNotification$.pipe(takeUntil(this.destroy$)).subscribe((response: any) => {
      this.notifications = response["data"]["notifications"];
    });

    // this.socketService.listen("message").subscribe((message: any) => {
    //   this.notifications.unshift(message);
    //   this.notificationService.playAudio();
    // })

    this.signalRService.getMessages().pipe(takeUntil(this.destroy$)).subscribe((message: any) => {
      console.log("message received", message);
      // Check if notification already exists to prevent duplicates
      const existingNotification = this.notifications.find(n => n.id === message.id);
      if (!existingNotification) {
        this.notifications.unshift(message);
        this.notificationService.playAudio();
      }
      console.log("this.notifications", this.notifications.length);
    })

    this.directionService.direction$.pipe(takeUntil(this.destroy$)).subscribe((dir) => {
      this.dir = dir;
      if (dir == 'rtl') {
        this.currentLang = 'ar';
      } else {
        this.currentLang = 'en'
      }
    })

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(event => {
      this.currentLang = event.lang;

    });

    this.strategyService.selectedStrategy$.pipe(takeUntil(this.destroy$)).subscribe((strategy) => {
      this.selectedStrategy = strategy as Strategy;
    });
    this.spinnerService.progressSource$.pipe(takeUntil(this.destroy$)).subscribe(progress => {
      let final = progress * 100
      if (final > 100) {
        final = 0
      } else {
        this.progress = progress * 100
      }
    });

    this.observer.observe(['(max-width: 800px)']).pipe(takeUntil(this.destroy$)).subscribe((screenSize) => {
      if (screenSize.matches) {
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }
    });
  };

  ngAfterViewInit() {
    // Language toggle initialization no longer needed with button implementation
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up the listener when the component is destroyed
    if (this.unlistenDocumentClick) {
      this.unlistenDocumentClick();
    }
  }

  toggoleTheme() {
    this.themeService.switchTheme(this.theme == 'theme' ? 'lara-dark-purple' : 'theme');
  }

  toggoleUserListInfo() {
    this.unlistenDocumentClick = this.renderer.listen('document', 'click', this.onDocumentClick.bind(this));
    this.ListInfo = !this.ListInfo;
  }

  toggoleNotification() {
    this.notificationMenu = !this.notificationMenu;
  }

  logOut() {
    this.ListInfo = false;
    this.socketService.disconnect();
    this.authService.logout();
    this.router.navigateByUrl('/login');
  }

  markAsRead(notificationId: number) {
    const notification = this.notifications.find(n => n.id == notificationId) as INotification
    if (notification.is_read) {
      return;
    } else {

      this.notificationService.markAsRead(notificationId).subscribe(() => {
        notification["is_read"] = true
      })
    }
  }

  navigateToEntity(notification: INotification) {
    if (notification.type == 'assessment_response' || notification.type == 'submission_request') {
      // this.router.navigateByUrl(`strategies/${this.se}/projects/48`)
    }
  }

  allNotifications() {
    this.notificationService.setNotificationSidebar(true);
  }


  changeLanguage(lang: string) {
    this.translate.use(lang);
    this.toggleDirection(lang == "ar" ? 'rtl' : "ltr");
  }

  toggleDirection(dir: "rtl" | "ltr") {
    this.directionService.setDirection(dir);
  }

  private unlistenDocumentClick!: () => void;
  toggoleShortLinksMenu() {
    this.unlistenDocumentClick = this.renderer.listen('document', 'click', this.onDocumentClick.bind(this));
    this.isShortLinksMenu = !this.isShortLinksMenu;
  }

  onDocumentClick(event: MouseEvent) {

    if (this.isShortLinksMenu && !this.elRef.nativeElement.contains(event.target)) {
      this.isShortLinksMenu = false;

      // Stop listening for document clicks
      this.unlistenDocumentClick();
    }
    if (this.ListInfo && !this.elRef.nativeElement.contains(event.target)) {
      this.ListInfo = false;
      // Stop listening for document clicks
      this.unlistenDocumentClick();
    }
  }



  toggleLanguage(event: any) {
    console.log("toggleLanguage")
    const toggleElement = event.target as HTMLInputElement;
    const isChecked = toggleElement.checked;
    console.log("isChecked", isChecked);
    if (!isChecked) {
      this.translate.use('ar');
      this.toggleDirection('rtl');
      this.currentLang = 'ar';
      localStorage.setItem('lang', 'ar');
    } else {
      this.translate.use('en');
      this.toggleDirection('ltr');
      this.currentLang = "en";
      localStorage.setItem('lang', 'en');
    }
    // Reload the page to refresh all data with new language
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  toggleLanguageButton() {
    console.log("toggleLanguageButton - current lang:", this.currentLang);
    if (this.currentLang === 'ar') {
      this.translate.use('en');
      this.toggleDirection('ltr');
      this.currentLang = 'en';
      localStorage.setItem('lang', 'en');
    } else {
      this.translate.use('ar');
      this.toggleDirection('rtl');
      this.currentLang = 'ar';
      localStorage.setItem('lang', 'ar');
    }
    // Reload the page to refresh all data with new language
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  getContainerStyle() {
    if (this.isMobile) {
      const paddingLeft = this.dir === 'ltr' && this.user ? '70px' : '0';
      const paddingRight = this.dir === 'rtl' && this.user ? '70px' : '0';
      return {
        'padding-left': paddingLeft,
        'padding-right': paddingRight
      };
    }
    const paddingLeft = this.dir === 'ltr' && this.user ? '250px' : '0';
    const paddingRight = this.dir === 'rtl' && this.user ? '250px' : '0';

    return {
      'padding-left': paddingLeft,
      'padding-right': paddingRight
    };
  }
  getContainerClass() {
    return {
      'container__ar': this.dir === 'rtl',
      'container__en': this.dir === 'ltr',
    };
  }
};